/**

 * Category Drill-Down Navigation (Ozon-style)

 * =============================================

 * Transforms the flat Shopify category filter into a hierarchical

 * drill-down navigation with breadcrumbs.

 * Supports BOTH desktop sidebar and mobile drawer filters.

 */



(function () {

  'use strict';



  // --- Configuration ---

  const CONFIG = Object.assign(

    {

      filterIndices: [

        'vertical-filter.p.t.category',

        'drawer-filter.p.t.category'

      ],

      rootLabel: 'All categories',

      showCount: true,

      filterAsLinks: true,

    },

    window.DRILLDOWN_CONFIG || {}

  );



  // Support old single filterIndex config

  if (window.DRILLDOWN_CONFIG && window.DRILLDOWN_CONFIG.filterIndex) {

    CONFIG.filterIndices = [window.DRILLDOWN_CONFIG.filterIndex];

    if (!CONFIG.filterIndices.includes('drawer-filter.p.t.category')) {

      CONFIG.filterIndices.push('drawer-filter.p.t.category');

    }

    if (!CONFIG.filterIndices.includes('vertical-filter.p.t.category')) {

      CONFIG.filterIndices.push('vertical-filter.p.t.category');

    }

  }



  // --- Styles ---

  const STYLES = `

    .drilldown-nav {

      font-size: 14px;

      line-height: 1.4;

    }



    .drilldown-breadcrumb {

      display: flex;

      align-items: center;

      gap: 4px;

      padding: 8px 0;

      cursor: pointer;

      color: var(--color-foreground, #333);

      text-decoration: none;

      transition: opacity 0.15s;

    }



    .drilldown-breadcrumb:hover {

      opacity: 0.7;

    }



    .drilldown-breadcrumb svg {

      flex-shrink: 0;

      width: 16px;

      height: 16px;

    }



    .drilldown-breadcrumb span {

      font-size: 14px;

    }



    .drilldown-breadcrumb--active {

      font-weight: 600;

      cursor: default;

    }



    .drilldown-breadcrumb--active:hover {

      opacity: 1;

    }



    .drilldown-children {

      padding-left: 8px;

      margin-top: 4px;

      border-left: 2px solid var(--color-border, #e5e5e5);

    }



    .drilldown-item {

      display: flex;

      align-items: center;

      justify-content: space-between;

      gap: 8px;

      padding: 8px 12px;

      cursor: pointer;

      color: var(--color-foreground, #333);

      text-decoration: none;

      border-radius: 6px;

      transition: background-color 0.15s;

    }



    .drilldown-item:hover {

      background-color: var(--color-background-contrast, #f5f5f5);

    }



    .drilldown-item--active {

      font-weight: 600;

      background-color: var(--color-background-contrast, #f0f0f0);

    }



    .drilldown-item__label {

      flex: 1;

    }



    .drilldown-item__count {

      font-size: 12px;

      color: var(--color-foreground-secondary, #888);

      flex-shrink: 0;

    }



    .drilldown-item__arrow {

      width: 14px;

      height: 14px;

      flex-shrink: 0;

      opacity: 0.5;

    }

  `;



  // --- SVG Icons ---

  const CHEVRON_LEFT = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12.5 15L7.5 10L12.5 5"/></svg>`;

  const CHEVRON_RIGHT = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7.5 5L12.5 10L7.5 15"/></svg>`;



  // --- Tree Building ---



  function parseCategoryItems(accordionContent) {

    const items = [];

    const listItems = accordionContent.querySelectorAll('li');



    listItems.forEach((li) => {

      const input = li.querySelector('input');

      const labelText = li.querySelector('.reversed-link__text');

      const countEl = li.querySelector('.count');



      if (input && labelText) {

        items.push({

          value: input.value.trim(),

          label: labelText.textContent.trim(),

          count: countEl ? parseInt(countEl.textContent.trim(), 10) || 0 : 0,

        });

      }

    });



    return items;

  }



  function findDirectParent(item, allItems) {

    const parts = item.value.split('-');



    for (let len = parts.length - 1; len >= 1; len--) {

      const candidateValue = parts.slice(0, len).join('-');

      const parent = allItems.find((i) => i.value === candidateValue);

      if (parent) {

        return parent;

      }

    }



    return null;

  }



  function buildTree(items) {

    const nodeMap = new Map();



    items.forEach((item) => {

      nodeMap.set(item.value, {

        value: item.value,

        label: item.label,

        count: item.count,

        children: [],

        parent: null,

      });

    });



    const roots = [];



    items.forEach((item) => {

      const node = nodeMap.get(item.value);

      const parentItem = findDirectParent(item, items);



      if (parentItem) {

        const parentNode = nodeMap.get(parentItem.value);

        node.parent = parentNode;

        parentNode.children.push(node);

      } else {

        roots.push(node);

      }

    });



    function sortChildren(nodes) {

      nodes.sort((a, b) => a.label.localeCompare(b.label));

      nodes.forEach((n) => sortChildren(n.children));

    }

    sortChildren(roots);



    return { roots, nodeMap };

  }



  // --- Rendering ---



  function getAncestorChain(node) {

    const chain = [];

    let current = node;

    while (current) {

      chain.unshift(current);

      current = current.parent;

    }

    return chain;

  }



  function buildFilterUrl(categoryValue) {

    const url = new URL(window.location.href);

    url.searchParams.delete('filter.p.t.category');

    url.searchParams.set('filter.p.t.category', categoryValue);

    url.searchParams.delete('page');

    return url.toString();

  }



  function buildResetUrl() {

    const url = new URL(window.location.href);

    url.searchParams.delete('filter.p.t.category');

    url.searchParams.delete('page');

    return url.toString();

  }



  function getActiveCategory() {

    const url = new URL(window.location.href);

    return url.searchParams.get('filter.p.t.category') || null;

  }



  function renderDrilldown(container, tree) {

    const activeValue = getActiveCategory();

    const activeNode = activeValue ? tree.nodeMap.get(activeValue) : null;



    let html = '<div class="drilldown-nav">';



    if (activeNode) {

      const chain = getAncestorChain(activeNode);



      html += `<a class="drilldown-breadcrumb" href="${buildResetUrl()}">${CHEVRON_LEFT}<span>${CONFIG.rootLabel}</span></a>`;



      chain.forEach((ancestor, idx) => {

        const isLast = idx === chain.length - 1;



        if (isLast) {

          html += `<div class="drilldown-breadcrumb drilldown-breadcrumb--active">${CHEVRON_LEFT}<span>${ancestor.label}</span></div>`;

        } else {

          html += `<a class="drilldown-breadcrumb" href="${buildFilterUrl(ancestor.value)}">${CHEVRON_LEFT}<span>${ancestor.label}</span></a>`;

        }

      });



      const displayItems = activeNode.children.length > 0 ? activeNode.children : activeNode.parent ? activeNode.parent.children : [];



      if (displayItems.length > 0) {

        html += '<div class="drilldown-children">';

        displayItems.forEach((child) => {

          const isActive = child.value === activeValue;

          const hasChildren = child.children.length > 0;

          const activeClass = isActive ? ' drilldown-item--active' : '';



          html += `<a class="drilldown-item${activeClass}" href="${buildFilterUrl(child.value)}">`;

          html += `<span class="drilldown-item__label">${child.label}</span>`;

          if (CONFIG.showCount) {

            html += `<span class="drilldown-item__count">${child.count}</span>`;

          }

          if (hasChildren) {

            html += `<span class="drilldown-item__arrow">${CHEVRON_RIGHT}</span>`;

          }

          html += '</a>';

        });

        html += '</div>';

      }

    } else {

      html += `<div class="drilldown-breadcrumb drilldown-breadcrumb--active"><span>${CONFIG.rootLabel}</span></div>`;

      html += '<div class="drilldown-children">';



      tree.roots.forEach((root) => {

        const hasChildren = root.children.length > 0;



        html += `<a class="drilldown-item" href="${buildFilterUrl(root.value)}">`;

        html += `<span class="drilldown-item__label">${root.label}</span>`;

        if (CONFIG.showCount) {

          html += `<span class="drilldown-item__count">${root.count}</span>`;

        }

        if (hasChildren) {

          html += `<span class="drilldown-item__arrow">${CHEVRON_RIGHT}</span>`;

        }

        html += '</a>';

      });



      html += '</div>';

    }



    html += '</div>';

    container.innerHTML = html;

  }



  // --- Initialization ---



  function initSingle(filterIndex) {

    const accordion = document.querySelector(

      `[data-index="${filterIndex}"]`

    );



    if (!accordion) return false;



    // Skip if already initialized

    if (accordion.querySelector('.drilldown-nav')) return true;



    const content = accordion.querySelector('.accordion-details__content');

    if (!content) return false;



    const items = parseCategoryItems(content);

    if (items.length === 0) return false;



    const tree = buildTree(items);



    // Make sure accordion is open

    accordion.setAttribute('open', '');



    // Replace content

    renderDrilldown(content, tree);



    // Hide "Show more" button

    const showMoreBtn = accordion.querySelector('show-more-button');

    if (showMoreBtn) {

      showMoreBtn.style.display = 'none';

    }



    return true;

  }



  function init() {

    // Inject styles once

    if (!document.getElementById('drilldown-styles')) {

      const style = document.createElement('style');

      style.id = 'drilldown-styles';

      style.textContent = STYLES;

      document.head.appendChild(style);

    }



    let totalInit = 0;

    CONFIG.filterIndices.forEach((filterIndex) => {

      if (initSingle(filterIndex)) {

        totalInit++;

      }

    });



    if (totalInit > 0) {

      console.log('[Drilldown] Initialized', totalInit, 'filter(s):', CONFIG.filterIndices.slice(0, totalInit).join(', '));

    }

  }



  // Run on DOM ready

  if (document.readyState === 'loading') {

    document.addEventListener('DOMContentLoaded', init);

  } else {

    setTimeout(init, 100);

  }



  // Re-initialize after Shopify AJAX filter updates

  const observer = new MutationObserver((mutations) => {

    for (const mutation of mutations) {

      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {

        let needsInit = false;

        CONFIG.filterIndices.forEach((filterIndex) => {

          const accordion = document.querySelector(`[data-index="${filterIndex}"]`);

          if (accordion && !accordion.querySelector('.drilldown-nav')) {

            needsInit = true;

          }

        });

        if (needsInit) {

          setTimeout(init, 50);

          break;

        }

      }

    }

  });



  const startObserving = () => {

    // Observe desktop sidebar

    document.querySelectorAll('.facet-form, .face-form').forEach((form) => {

      observer.observe(form, { childList: true, subtree: true });

    });



    // Observe mobile drawer

    const drawer = document.getElementById('DrawerFacetFiltersContainer');

    if (drawer) {

      observer.observe(drawer, { childList: true, subtree: true });

    }



    // Also observe drawer opening (content may load lazily)

    const facetDrawer = document.getElementById('FacetDrawer');

    if (facetDrawer) {

      const drawerObserver = new MutationObserver(() => {

        setTimeout(init, 100);

      });

      drawerObserver.observe(facetDrawer, { attributes: true, attributeFilter: ['hidden', 'open', 'aria-expanded'] });

    }

  };



  if (document.readyState === 'loading') {

    document.addEventListener('DOMContentLoaded', startObserving);

  } else {

    startObserving();

  }



  // Listen for Shopify section events

  document.addEventListener('shopify:section:load', () => setTimeout(init, 100));

})();